import React from 'react';
import { Circle, G, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { RoomBox } from '../primitives/RoomBox';
import { DT } from '../tokens';

/** ISO 14644-1 Particle Count — class limit, sample volume, grid of sample locations */
export function DiagramCleanroomParticleCount() {
  // Room: grid of sample locations (matches Sampling Locations diagram pattern)
  const rows = 3;
  const cols = 4;
  const x0 = 80;
  const y0 = 85;
  const w = 160;
  const h = 100;
  const dx = w / (cols - 1);
  const dy = h / (rows - 1);

  // Faint particle haze behind grid (context: these are what the counter measures)
  const particles: [number, number, number][] = [
    [90, 100, 1.3], [125, 118, 1.1], [155, 92, 1.3], [195, 125, 1.2],
    [115, 150, 1.0], [165, 160, 1.3], [210, 102, 1.1], [145, 135, 0.9],
    [95, 170, 1.2], [180, 108, 1.0], [135, 180, 1.1], [215, 170, 1.0],
    [105, 130, 1.0], [200, 155, 1.1],
  ];

  // Active sample location (where the probe currently is — highlighted)
  const activeRow = 1;
  const activeCol = 2;

  const points: React.ReactNode[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const px = x0 + dx * c;
      const py = y0 + dy * r;
      const isActive = r === activeRow && c === activeCol;
      points.push(
        <G key={`${r}-${c}`}>
          <Circle
            cx={px}
            cy={py}
            r={isActive ? 6 : 5}
            fill="none"
            stroke={DT.varColor}
            strokeWidth={isActive ? 2 : 1.2}
          />
          <Circle cx={px} cy={py} r={isActive ? 2.5 : 1.8} fill={DT.varColor} />
          <SvgText
            x={px + 8}
            y={py + 4}
            fill={DT.dim}
            fontSize="7"
            fontFamily={DT.mono}
          >
            {r * cols + c + 1}
          </SvgText>
        </G>,
      );
    }
  }

  // Probe + counter near active location
  const probeX = x0 + dx * activeCol;
  const probeY = y0 + dy * activeRow;

  return (
    <DiagramCanvas>
      <RoomBox x={60} y={65} width={210} height={150} />

      {/* Faint particle haze */}
      {particles.map(([px, py, r], i) => (
        <Circle key={`p${i}`} cx={px} cy={py} r={r} fill={DT.varColor} opacity={0.25} />
      ))}

      {/* Sample location grid */}
      {points}

      {/* Probe body entering from the right into the active location */}
      <G>
        <Rect
          x={probeX + 6}
          y={probeY - 3}
          width={70}
          height={6}
          fill={DT.fill}
          stroke={DT.stroke}
          strokeWidth="1"
        />
      </G>

      {/* Counter instrument */}
      <G>
        <Rect x={285} y={probeY - 17} width={38} height={34} rx={3} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.2" />
        <Rect x={289} y={probeY - 13} width={30} height={10} fill={DT.bg} stroke={DT.varColor} strokeWidth="0.8" />
        <SvgText x={304} y={probeY - 5} fill={DT.varColor} fontSize="7" fontFamily={DT.mono} textAnchor="middle">
          COUNT
        </SvgText>
        <Circle cx={295} cy={probeY + 7} r={1.8} fill={DT.varColor} />
        <Circle cx={304} cy={probeY + 7} r={1.8} fill={DT.dim} />
        <Circle cx={313} cy={probeY + 7} r={1.8} fill={DT.dim} />
      </G>

      {/* Annotations — all outside room, above the formula tag (y<225) */}
      <LeaderLine
        x1={x0}
        y1={y0}
        x2={18}
        y2={42}
        varName="N_L"
        desc="Sample locations"
        anchor="start"
      />
      <LeaderLine
        x1={x0 + dx * 3}
        y1={y0 + dy * 2}
        x2={18}
        y2={210}
        varName="C_n"
        desc="Class limit"
        anchor="start"
      />
      <LeaderLine
        x1={probeX}
        y1={probeY - 6}
        x2={330}
        y2={65}
        varName="V_s"
        desc="Sample vol."
        anchor="end"
      />

      <FormulaTag formula="C_n = 10^N × (0.1 / D)^2.08" />
    </DiagramCanvas>
  );
}
